// Motor de búsqueda de jugadas para la IA. Lógica pura y reutilizable
// (se puede ejecutar en el hilo principal o dentro de un Web Worker).
import { normalizeForLookup } from '../utils/unicode.js';
import { TILE_TYPE } from '../utils/constants.js';

const MAX_INDEX_LEN = 16; // no indexamos palabras más largas (la IA no puede formarlas)

// Índice por longitud del diccionario, cacheado por REFERENCIA de array. Como el validador
// devuelve siempre el mismo array (aiWordList) y el worker lo cachea, se construye una vez.
const LEN_INDEX = new WeakMap();
function lengthIndex(dictionary) {
  let idx = LEN_INDEX.get(dictionary);
  if (idx) return idx;
  const byLen = new Map();
  let maxWordLen = 0;
  for (const w of dictionary) {
    const L = w.length;
    if (L < 2 || L > MAX_INDEX_LEN) continue;
    let b = byLen.get(L);
    if (!b) { b = []; byLen.set(L, b); }
    b.push(w);
    if (L > maxWordLen) maxWordLen = L;
  }
  idx = { byLen, maxWordLen };
  LEN_INDEX.set(dictionary, idx);
  return idx;
}

/**
 * Intenta cubrir una palabra objetivo (normalizada) usando un multiconjunto de fichas.
 * Respeta prefijos (solo al inicio) y sufijos (solo al final).
 * @returns {object[]|null} secuencia de fichas (en orden) o null si no es posible.
 */
function tileWord(targetNorm, tiles, pos = 0, used = new Set()) {
  if (pos === targetNorm.length) return [];

  for (const tile of tiles) {
    if (used.has(tile.instanceId)) continue;

    // Comodín universal: cubre exactamente un carácter cualquiera.
    if (tile.type === TILE_TYPE.WILDCARD && tile.wildKind === 'universal') {
      used.add(tile.instanceId);
      const rest = tileWord(targetNorm, tiles, pos + 1, used);
      if (rest) {
        const clone = { ...tile, assignedText: (targetNorm[pos] || '').toUpperCase() };
        return [clone, ...rest];
      }
      used.delete(tile.instanceId);
      continue;
    }
    if (tile.type === TILE_TYPE.WILDCARD) continue; // otros comodines no se usan en la búsqueda

    const tnorm = normalizeForLookup(tile.text);
    if (!tnorm) continue;
    if (!targetNorm.startsWith(tnorm, pos)) continue;

    const endPos = pos + tnorm.length;
    if (tile.type === TILE_TYPE.PREFIX && pos !== 0) continue;
    if (tile.type === TILE_TYPE.SUFFIX && endPos !== targetNorm.length) continue;

    used.add(tile.instanceId);
    const rest = tileWord(targetNorm, tiles, endPos, used);
    if (rest) return [tile, ...rest];
    used.delete(tile.instanceId);
  }
  return null;
}

/** Puntúa una jugada. Con `emptyBias`, la IA busca VACIAR el atril (así es como se gana):
 *  prioriza soltar el máximo de fichas y da un bonus enorme a quedarse sin fichas. */
function scorePlay(seq, wordNorm, nonWildRack, opts) {
  if (!opts.emptyBias) {
    // EASY: comportamiento clásico (por longitud + afijos), para seguir siendo flojo.
    let s = wordNorm.length;
    for (const t of seq) {
      if (t.type === TILE_TYPE.PREFIX || t.type === TILE_TYPE.SUFFIX) s += 3;
      if (t.type === TILE_TYPE.SYLLABLE) s += 1;
    }
    if (opts.preferLong) s += wordNorm.length;
    return s;
  }
  // NORMAL / HARD: optimizar el vaciado del atril.
  let nonWildUsed = 0;
  for (const t of seq) if (t.type !== TILE_TYPE.WILDCARD) nonWildUsed++;
  let s = seq.length * 100;                       // cuantas más fichas suelte, mejor
  if (nonWildUsed >= nonWildRack) s += 100000;    // deja el atril vacío -> ¡gana la partida!
  s += wordNorm.length * 2;                        // desempate: palabras más largas
  for (const t of seq) {
    if (t.type === TILE_TYPE.PREFIX || t.type === TILE_TYPE.SUFFIX) s += 2;
    if (t.type === TILE_TYPE.SYLLABLE) s += 1;
  }
  return s;
}

/**
 * Busca la mejor palabra que la IA puede construir con su atril.
 * @param {object} params
 * @param {object[]} params.rack fichas de la mano
 * @param {string[]} params.dictionary palabras normalizadas candidatas
 * @param {object} params.opts { maxLen, minLen, preferLong, emptyBias }
 * @returns {{ word:string, tiles:object[], score:number } | null}
 */
export function findBestWord({ rack, dictionary, opts = {} }) {
  const maxLen = opts.maxLen ?? 8;
  const minLen = opts.minLen ?? 2;

  const wilds = rack.filter((t) => t.type === TILE_TYPE.WILDCARD && t.wildKind === 'universal').length;
  let charCap = wilds;
  let nonWildRack = 0;
  for (const t of rack) {
    if (t.type === TILE_TYPE.WILDCARD) continue;
    const n = normalizeForLookup(t.text);
    if (n) charCap += n.length;
    nonWildRack++;
  }

  const idx = lengthIndex(dictionary);
  const hi = Math.min(maxLen, charCap, idx.maxWordLen);

  // `randomize` (nivel Normal): en vez de jugar SIEMPRE la mejor palabra, mantiene un
  // grupo con las mejores y elige una al azar → juega bien pero no de forma perfecta.
  const randomize = opts.randomize ?? false;
  const poolSize = randomize ? (opts.poolSize ?? 12) : 1;

  let best = null;
  let pool = []; // top-K por puntuación (ascendente); pool[0] = la peor del grupo
  let examined = 0;
  const cap = opts.maxExamined ?? 400000; // salvaguarda de rendimiento

  // De las palabras más largas a las más cortas (soltar más fichas antes).
  for (let L = hi; L >= minLen && examined < cap; L--) {
    const bucket = idx.byLen.get(L);
    if (!bucket) continue;
    for (let i = 0; i < bucket.length; i++) {
      if (++examined >= cap) break;
      const wordNorm = bucket[i];
      const seq = tileWord(wordNorm, rack);
      if (!seq) continue;
      const s = scorePlay(seq, wordNorm, nonWildRack, opts);
      if (!best || s > best.score) best = { word: wordNorm, tiles: seq, score: s };
      if (randomize) {
        if (pool.length < poolSize) {
          pool.push({ word: wordNorm, tiles: seq, score: s });
          if (pool.length === poolSize) pool.sort((a, b) => a.score - b.score);
        } else if (s > pool[0].score) {
          pool[0] = { word: wordNorm, tiles: seq, score: s };
          pool.sort((a, b) => a.score - b.score);
        }
      }
    }
  }
  if (randomize && pool.length) return pool[Math.floor(Math.random() * pool.length)];
  return best;
}

/**
 * Intenta EVOLUCIONAR una palabra existente del tablero añadiendo/anteponiendo una ficha
 * de la mano. Comprueba contra el diccionario COMPLETO (validador). Suelta 1 ficha.
 * @returns {{ wordId:string, prepend:object[], append:object[], result:string } | null}
 */
export function findEvolution({ boardWords, rack, validator }) {
  if (!validator) return null;
  for (const bw of boardWords) {
    const base = bw.tiles.map((t) => t.text).join('');
    for (const tile of rack) {
      if (tile.type === TILE_TYPE.WILDCARD) continue;
      // Añadir al final (sufijos, letras o sílabas): CASA -> CASAS / CASA -> CASAR.
      if (tile.type === TILE_TYPE.SUFFIX || tile.type === TILE_TYPE.LETTER || tile.type === TILE_TYPE.SYLLABLE) {
        if (validator.existsInDictionary(base + tile.text)) {
          return { wordId: bw.id, prepend: [], append: [tile], result: base + tile.text };
        }
      }
      // Anteponer (prefijos, letras o sílabas).
      if (tile.type === TILE_TYPE.PREFIX || tile.type === TILE_TYPE.LETTER || tile.type === TILE_TYPE.SYLLABLE) {
        if (validator.existsInDictionary(tile.text + base)) {
          return { wordId: bw.id, prepend: [tile], append: [], result: tile.text + base };
        }
      }
    }
  }
  return null;
}
